import { z } from 'zod';

/**
 * Função auxiliar para calcular o progresso do resultado-chave
 *
 * IMPORTANTE: Esta função permite valores > 100% quando a meta é ultrapassada.
 * O valor mínimo é 0% (não permite progresso negativo).
 */
export function calculateKeyResultProgress(
  initialValue: number,
  currentValue: number,
  targetValue: number
): number {
  if (initialValue === targetValue) {
    throw new Error('O valor inicial e o valor alvo não podem ser iguais');
  }

  const progress = ((currentValue - initialValue) / (targetValue - initialValue)) * 100;

  // Permite progresso > 100% quando meta é ultrapassada
  // Apenas previne progresso negativo
  return Math.max(0, progress);
}

/**
 * Base schema for key result (without refinements)
 */
const KeyResultBaseSchema = z.object({
  title: z.string().min(5, 'O título deve ter no mínimo 5 caracteres').max(150, 'O título deve ter no máximo 150 caracteres'),
  description: z.string().max(500, 'A descrição deve ter no máximo 500 caracteres').optional(),
  objectiveId: z.string().min(1, 'O ID do objetivo é obrigatório'),
  objectiveTitle: z.string().min(1, 'O título do objetivo é obrigatório'),
  cycleId: z.string().min(1, 'O ID do ciclo é obrigatório'),
  metricType: z.enum(['number', 'percentage', 'currency', 'boolean']).default('number'),
  unit: z.string().max(20, 'A unidade deve ter no máximo 20 caracteres').optional(),
  initialValue: z.number().finite('O valor inicial deve ser um número finito'),
  targetValue: z.number().finite('O valor alvo deve ser um número finito'),
  currentValue: z.number().finite('O valor atual deve ser um número finito').optional(),
  status: z.enum(['on_track', 'at_risk', 'off_track', 'completed']).default('on_track'),
  responsibleId: z.string().min(1, 'O ID do responsável é obrigatório'),
  responsibleName: z.string().min(1, 'O nome do responsável é obrigatório'),
  responsibleEmail: z.string().email('Formato de e-mail inválido'),
  impediments: z.string().max(1000, 'Os impedimentos devem ter no máximo 1000 caracteres').optional(),
  notes: z.string().max(2000, 'As observações devem ter no máximo 2000 caracteres').optional(),
  startDate: z.instanceof(Date),
  targetDate: z.instanceof(Date)
});

/**
 * Schema Zod para criar um novo resultado-chave
 */
export const KeyResultCreateSchema = KeyResultBaseSchema.refine(
  (data) => data.initialValue !== data.targetValue,
  {
    message: 'O valor inicial e o valor alvo devem ser diferentes',
    path: ['targetValue']
  }
).refine(
  (data) => {
    // Valida que o título descreve um resultado, não uma tarefa
    const actionVerbs = ['implement', 'create', 'build', 'develop', 'write', 'code', 'make'];
    const lowerTitle = data.title.toLowerCase();
    const hasActionVerb = actionVerbs.some(verb => lowerTitle.startsWith(verb));

    return !hasActionVerb;
  },
  {
    message: 'O título do resultado-chave deve descrever um resultado mensurável, não uma tarefa',
    path: ['title']
  }
).refine(
  (data) => data.startDate < data.targetDate,
  {
    message: 'A data de início deve ser anterior à data alvo',
    path: ['targetDate']
  }
);

export type KeyResultCreate = z.infer<typeof KeyResultCreateSchema>;

/**
 * Schema Zod para atualizar um resultado-chave existente
 *
 * IMPORTANTE: Progresso pode exceder 100% quando meta é ultrapassada
 */
export const KeyResultUpdateSchema = KeyResultBaseSchema.partial().extend({
  id: z.string().min(1, 'O ID do resultado-chave é obrigatório'),
  progress: z.number().min(0, 'O progresso deve ser no mínimo 0').optional(),
  lastCheckInDate: z.instanceof(Date).nullable().optional(),
  checkInCount: z.number().min(0, 'A contagem de check-ins não pode ser negativa').optional(),
  completedAt: z.instanceof(Date).nullable().optional()
});

export type KeyResultUpdate = z.infer<typeof KeyResultUpdateSchema>;

/**
 * Schema Zod completo para entidade de resultado-chave com todos os campos
 *
 * IMPORTANTE: Progresso pode exceder 100% quando meta é ultrapassada
 */
export const KeyResultSchema = KeyResultBaseSchema.extend({
  id: z.string().min(1, 'O ID do resultado-chave é obrigatório'),
  currentValue: z.number().finite('O valor atual deve ser um número finito'),
  progress: z.number().min(0).default(0),
  lastCheckInDate: z.instanceof(Date).nullable().optional(),
  checkInCount: z.number().min(0).default(0),
  completedAt: z.instanceof(Date).nullable().optional(),
  createdAt: z.instanceof(Date),
  updatedAt: z.instanceof(Date),
  _metadata: z.object({
    version: z.number().int().positive().default(1),
    lastModifiedBy: z.string().min(1, 'O campo de última modificação por é obrigatório')
  })
});

export type KeyResult = z.infer<typeof KeyResultSchema>;

/**
 * Form-specific validation schema for Key Result forms
 * Used with React Hook Form for client-side validation
 * Includes Portuguese error messages for better UX
 */
export const KeyResultFormSchema = z
  .object({
    title: z
      .string()
      .min(3, 'Título deve ter pelo menos 3 caracteres')
      .max(200, 'Título não pode ter mais de 200 caracteres')
      .trim(),

    objectiveId: z
      .string({
        required_error: 'Objetivo é obrigatório',
      })
      .min(1, 'Objetivo é obrigatório'),

    type: z.enum(['percentage', 'number', 'currency', 'boolean'], {
      required_error: 'Tipo é obrigatório',
      invalid_type_error: 'Tipo inválido',
    }),

    unit: z
      .string()
      .max(50, 'Unidade não pode ter mais de 50 caracteres')
      .trim()
      .optional()
      .or(z.literal('')),

    baseline: z.number({
      required_error: 'Valor inicial é obrigatório',
      invalid_type_error: 'Valor inicial deve ser um número',
    }),

    target: z.number({
      required_error: 'Meta é obrigatória',
      invalid_type_error: 'Meta deve ser um número',
    }),

    current: z.number({
      required_error: 'Valor atual é obrigatório',
      invalid_type_error: 'Valor atual deve ser um número',
    }),

    dueDate: z
      .coerce.date({
        invalid_type_error: 'Data de vencimento inválida',
      })
      .optional()
      .nullable(),
  })
  .refine((data) => data.target !== data.baseline, {
    message: 'Meta deve ser diferente do valor inicial',
    path: ['target'],
  });

/**
 * TypeScript type inferred from KeyResultFormSchema
 */
export type KeyResultFormData = z.infer<typeof KeyResultFormSchema>;

/**
 * Default values for Key Result form initialization
 */
export const keyResultFormDefaults: Partial<KeyResultFormData> = {
  title: '',
  type: 'number',
  unit: '',
  baseline: 0,
  target: 100,
  current: 0,
};
